import React from 'react';
import { useTheme } from '@emotion/react';
import { Box, FormControlLabel, Grid, IconButton, Paper, Typography } from '@mui/material';
import { ReactComponent as CloseIcon } from "assets/close_icon.svg";
import ModalComponent from 'ui-components/ModalComponent';
import CustomInputField from 'ui-components/CustomInputField';
import { Primary, Secondary } from 'ui-components/Buttons';
import { PERIODS, placeholderPlanId, isPlaceholderConfig } from './PriceMatrix';
import PlanCheckbox from './PlanCheckbox';
import { regionName, regionCurrency } from './regions';

const PERIOD_LABELS = { monthly: 'Monthly', quarterly: 'Quarterly', yearly: 'Yearly' };

/**
 * Prices and Razorpay plan ids, behind a deliberate step.
 *
 * These are the only fields on this screen that can break a checkout, so they
 * are edited on purpose in a dialog rather than sitting open in the page
 * alongside a subtitle. The price shown here is display only — what a customer
 * is charged lives on the Razorpay plan behind the id.
 *
 * Laid out as the same region-by-period grid the summary uses, so the shape of
 * what you are editing matches the shape of what you were just reading. Nine
 * price fields stacked in a column all carried their own label, which made the
 * repeated words the loudest thing in the dialog; here the period is a column
 * heading, said once.
 */
const BillingModal = ({ open, handleClose, planKey, plan, onApply }) => {
    const theme = useTheme();
    const [draft, setDraft] = React.useState(plan);
    // A free plan hides the grid until someone says they want to charge for it.
    const [pricingOpen, setPricingOpen] = React.useState(false);

    React.useEffect(() => {
        if (open) {
            setDraft(plan);
            setPricingOpen(false);
        }
    }, [open, plan]);

    if (!open) return null;

    const regions = Object.keys(draft?.pricing || draft?.razorpay_config || { IN: {} });

    // A plan nobody is charged for has nothing to bill against: every price is
    // zero or absent, and no region sells any period. Offering eighteen empty
    // fields in that state suggests billing is simply unconfigured, when in
    // fact there is nothing to configure.
    const isFree = !regions.some((region) => PERIODS.some((period) =>
        draft?.pricing?.[region]?.[period] > 0
        || draft?.razorpay_config?.[region]?.[period]?.plan_id));

    const setPrice = (region, period, rawValue) => {
        const pricing = { ...(draft.pricing || {}) };
        const regionPricing = { ...(pricing[region] || {}) };
        // Cleared means "not sold for this period", which is an absent key
        // rather than a zero price.
        if (rawValue === '') delete regionPricing[period];
        else regionPricing[period] = Number(rawValue);
        pricing[region] = regionPricing;
        setDraft({ ...draft, pricing });
    };

    const setPlanId = (region, period, planId) => {
        const razorpay = { ...(draft.razorpay_config || {}) };
        const regionConfig = { ...(razorpay[region] || {}) };
        if (!planId.trim()) delete regionConfig[period];
        else regionConfig[period] = { ...(regionConfig[period] || {}), plan_id: planId.trim(), frequency: period };
        razorpay[region] = regionConfig;
        setDraft({ ...draft, razorpay_config: razorpay });
    };

    const regionIsUnsold = (region) => PERIODS.some((period) =>
        isPlaceholderConfig(draft?.razorpay_config?.[region]?.[period]));

    /**
     * A region is either being sold in or it is not — that is one decision, not
     * one per billing period, and it is the shape the question actually takes:
     * EU has prices on the page but has never launched.
     *
     * Switching it on writes a stand-in id for every priced period. The id
     * matters even though nothing can be bought with it: the upgrade button
     * looks for the `rzp_plan_` prefix and sends people to support rather than
     * a checkout that cannot complete. The flag records that this was
     * deliberate rather than a mistyped id.
     */
    const setRegionUnsold = (region, unsold) => {
        const razorpay = { ...(draft.razorpay_config || {}) };
        const regionConfig = { ...(razorpay[region] || {}) };

        PERIODS.forEach((period) => {
            if (unsold) {
                // Only periods with a price: an empty period is simply not on
                // the page, and needs no id at all.
                if (!draft?.pricing?.[region]?.[period]) return;
                regionConfig[period] = {
                    ...(regionConfig[period] || {}),
                    plan_id: placeholderPlanId(planKey, region, period),
                    frequency: period,
                    placeholder: true,
                };
            } else if (isPlaceholderConfig(regionConfig[period])) {
                // Cleared rather than kept, so a stand-in is never mistaken for
                // a real id once the region is being sold in.
                delete regionConfig[period];
            }
        });

        razorpay[region] = regionConfig;
        setDraft({ ...draft, razorpay_config: razorpay });
    };

    const columnTemplate = `minmax(8.5rem, 0.6fr) repeat(${PERIODS.length}, 1fr)`;
    const showPricingGrid = !isFree || pricingOpen;

    return (
        <ModalComponent open={open} onClose={handleClose} style={{ maxWidth: showPricingGrid ? 940 : 520, width: "100%" }}>
            <Paper sx={{ background: theme.palette.customColors.whiteBG, borderRadius: '8px' }}>
                <IconButton sx={{ position: "absolute", top: 20, right: 20, padding: 0 }} onClick={handleClose}>
                    <CloseIcon width={40} height={40} />
                </IconButton>

                <Grid display="grid" container padding={"1.5rem"} gap={"1.25rem"}>
                    <Grid item xs={12}>
                        <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: theme.palette.customColors.g300 }}>
                            Billing for {plan?.title || planKey}
                        </Typography>
                        {showPricingGrid && (
                            <Typography sx={{ fontSize: '0.875rem', color: theme.palette.customColors.g200, marginTop: 0.5, maxWidth: '68ch' }}>
                                The page shows the price. Razorpay charges the amount on the plan behind the id,
                                so change both together. An empty period is not sold.
                            </Typography>
                        )}
                    </Grid>

                    {!showPricingGrid ? (
                        <Grid item xs={12}>
                            <Typography sx={{ fontSize: '0.875rem', color: theme.palette.customColors.g200, maxWidth: '48ch' }}>
                                Free in every region. Add pricing to start charging for it.
                            </Typography>
                        </Grid>
                    ) : (
                    <Grid item xs={12} sx={{ overflowX: 'auto' }}>
                        <Box sx={{ minWidth: '46rem' }}>
                            <Box sx={{
                                display: 'grid',
                                gridTemplateColumns: columnTemplate,
                                gap: '1rem',
                                paddingBottom: '0.5rem',
                            }}>
                                <span />
                                {PERIODS.map((period) => (
                                    <Typography key={period} sx={{
                                        fontSize: '0.8125rem',
                                        fontWeight: 600,
                                        color: theme.palette.customColors.g500,
                                    }}>
                                        {PERIOD_LABELS[period]}
                                    </Typography>
                                ))}
                            </Box>

                            <Box display="grid" gap="0.75rem">
                                {regions.map((region) => {
                                    const unsold = regionIsUnsold(region);

                                    return (
                                    <Box key={region} sx={{
                                        display: 'grid',
                                        gridTemplateColumns: columnTemplate,
                                        gap: '1rem',
                                        padding: '0.875rem',
                                        borderRadius: '8px',
                                        border: `1px solid ${theme.palette.customColors.b50}`,
                                    }}>
                                        <Box>
                                            <Typography sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                                                {regionName(region)}
                                            </Typography>
                                            <Typography sx={{ fontSize: '0.75rem', color: theme.palette.customColors.g100 }}>
                                                {regionCurrency(draft?.pricing?.[region], region)}
                                            </Typography>
                                            <FormControlLabel
                                                sx={{ marginLeft: 0, marginTop: '0.5rem' }}
                                                control={
                                                    <PlanCheckbox
                                                        checked={unsold}
                                                        onChange={(event) => setRegionUnsold(region, event.target.checked)}
                                                    />
                                                }
                                                label={
                                                    <Typography sx={{
                                                        fontSize: '0.75rem',
                                                        color: theme.palette.customColors.g200,
                                                        whiteSpace: 'nowrap',
                                                    }}>
                                                        Not selling yet
                                                    </Typography>
                                                }
                                            />
                                        </Box>

                                        {PERIODS.map((period) => (
                                            <Box key={period}>
                                                <CustomInputField
                                                    type="number"
                                                    placeholder="Not sold"
                                                    value={draft?.pricing?.[region]?.[period] ?? ''}
                                                    onChange={(event) => setPrice(region, period, event.target.value)}
                                                    sx={{ marginBottom: unsold ? 0 : '0.375rem' }}
                                                />
                                                {/* A region that is not being sold in has no id worth
                                                    showing: the value is generated, and nothing can be
                                                    bought with it. */}
                                                {!unsold && (
                                                    <CustomInputField
                                                        placeholder="Razorpay plan ID"
                                                        value={draft?.razorpay_config?.[region]?.[period]?.plan_id ?? ''}
                                                        onChange={(event) => setPlanId(region, period, event.target.value)}
                                                        sx={{ marginBottom: 0 }}
                                                    />
                                                )}
                                            </Box>
                                        ))}

                                        {unsold && (
                                            <Typography sx={{
                                                gridColumn: '2 / -1',
                                                fontSize: '0.75rem',
                                                lineHeight: '2.75rem',
                                                color: theme.palette.customColors.g200,
                                            }}>
                                                Priced on the page, but nobody can buy here.
                                            </Typography>
                                        )}
                                    </Box>
                                    );
                                })}
                            </Box>
                        </Box>
                    </Grid>
                    )}

                    <Grid item xs={12} display="flex" justifyContent="flex-end" gap={1.5}>
                        {showPricingGrid ? (
                            <>
                                <Secondary onClick={handleClose} sx={{ padding: '0.625rem 1.5rem' }}>Cancel</Secondary>
                                <Primary sx={{ padding: '0.625rem 1.5rem' }} onClick={() => { onApply(draft); handleClose(); }}>
                                    Apply
                                </Primary>
                            </>
                        ) : (
                            <>
                                <Secondary onClick={handleClose} sx={{ padding: '0.625rem 1.5rem' }}>Close</Secondary>
                                <Primary sx={{ padding: '0.625rem 1.5rem' }} onClick={() => setPricingOpen(true)}>
                                    Add pricing
                                </Primary>
                            </>
                        )}
                    </Grid>
                </Grid>
            </Paper>
        </ModalComponent>
    );
};

export default BillingModal;
